import React, { createContext, useContext, useEffect, useState } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { PushNotifications } from '@capacitor/push-notifications';

type MobileContextType = {
  isNative: boolean;
  platform: string;
  appUrlOpen: string | null;
  notificationCount: number;
  hideStatusBar: () => Promise<void>;
  showStatusBar: () => Promise<void>;
  setupPushNotifications: () => Promise<void>;
};

const MobileContext = createContext<MobileContextType>({
  isNative: false,
  platform: 'web',
  appUrlOpen: null,
  notificationCount: 0,
  hideStatusBar: async () => {},
  showStatusBar: async () => {},
  setupPushNotifications: async () => {},
});

export const useMobile = () => useContext(MobileContext);

export const MobileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [appUrlOpen, setAppUrlOpen] = useState<string | null>(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();

  useEffect(() => {
    if (isNative) {
      // Hide the splash screen after initial load
      SplashScreen.hide();
      
      // Listen for deep links (app opened from URL)
      App.addListener('appUrlOpen', (data) => {
        setAppUrlOpen(data.url);
      });

      // Handle app state changes
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          // App came to foreground, refresh data if needed
          console.log('App is active');
        }
      });

      // Handle back button for Android
      if (platform === 'android') {
        App.addListener('backButton', ({ canGoBack }) => {
          if (!canGoBack) {
            App.exitApp();
          } else {
            window.history.back();
          }
        });
      }
    }

    return () => {
      if (isNative) {
        // Clean up listeners when component unmounts
        App.removeAllListeners();
      }
    };
  }, [isNative, platform]);

  const hideStatusBar = async () => {
    if (isNative) {
      await StatusBar.hide();
    }
  };

  const showStatusBar = async () => {
    if (isNative) {
      await StatusBar.show();
    }
  };

  const setupPushNotifications = async () => {
    if (!isNative) return;

    // Request permission to use push notifications
    const result = await PushNotifications.requestPermissions();
    
    if (result.receive === 'granted') {
      // Register with Apple / Google to receive push
      await PushNotifications.register();
      
      // Handle registration success
      PushNotifications.addListener('registration', (token) => {
        console.log('Push registration success:', token.value);
        // Send the token to your backend
      });
      
      // Handle registration error
      PushNotifications.addListener('registrationError', (error) => {
        console.error('Push registration failed:', error);
      });
      
      // Handle incoming notifications
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push notification received:', notification);
        setNotificationCount((prev) => prev + 1);
      });
      
      // Handle notifications opened
      PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('Push notification action performed:', notification.actionId, notification.notification);
        setNotificationCount(0);
      });
    }
  };

  const value = {
    isNative,
    platform,
    appUrlOpen,
    notificationCount,
    hideStatusBar,
    showStatusBar,
    setupPushNotifications,
  };

  return (
    <MobileContext.Provider value={value}>
      {children}
    </MobileContext.Provider>
  );
};
